import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, IconButton, List, ListItem, ListItemText, Menu, MenuItem, Paper, Typography } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import projectService, { Package } from '../../services/projectService';
import { useNotification } from '../layouts/Layout';
import MobileInquiryForm from './MobileInquiryForm';

interface MobilePackagesListProps {
  projectId: string;
  projectName: string;
}

const MobilePackagesList: React.FC<MobilePackagesListProps> = ({ projectId, projectName }) => {
  const { showNotification } = useNotification();
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selected, setSelected] = useState<Package | null>(null);
  const [inquiryOpen, setInquiryOpen] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const list = await projectService.getPackagesByProjectId(projectId);
        if (active) setPackages(list);
      } catch (e: any) {
        if (active) setError(e?.message || 'Błąd pobierania pakietów');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [projectId]);

  const formatK = useMemo(() => (v?: number) => {
    if (v === undefined || v === null) return '—';
    const k = Math.round(Number(v) / 1000);
    try { return new Intl.NumberFormat('pl-PL').format(k) + ' k'; } catch { return k + ' k'; }
  }, []);

  const openMenu = (e: React.MouseEvent<HTMLElement>, pkg: Package) => {
    e.stopPropagation();
    setSelected(pkg);
    setMenuAnchorEl(e.currentTarget);
  };
  const closeMenu = () => { setMenuAnchorEl(null); };

  const onOpenDocs = () => {
    if (!selected) return;
    const url = selected.package_documentation_link; // używamy wyłącznie linku do dokumentacji pakietu
    if (url) window.open(url, '_blank'); else showNotification('Brak linku package_documentation_link', 'info');
    closeMenu();
  };
  const onInquiry = () => {
    if (!selected) return;
    setInquiryOpen(true);
    closeMenu();
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Pakiety</Typography>
      {loading && (
        <Box sx={{ display:'flex', justifyContent:'center', py: 2 }}><CircularProgress size={20} /></Box>
      )}
      {error && (
        <Typography color="error" variant="body2" sx={{ mb: 1 }}>{error}</Typography>
      )}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <List disablePadding>
          {packages.map((p) => (
            <ListItem key={p.package_id}
              secondaryAction={
                <IconButton edge="end" aria-label="menu" onClick={(e)=>openMenu(e, p)}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              }
            >
              <ListItemText
                primary={`${p.package_id} — ${p.name}`}
                secondary={`Szacunek: ${formatK(p.estimated_value)}`}
                primaryTypographyProps={{ noWrap: true }}
                secondaryTypographyProps={{ noWrap: true }}
              />
            </ListItem>
          ))}
          {packages.length === 0 && !loading && (
            <ListItem>
              <ListItemText primary="Brak pakietów" />
            </ListItem>
          )}
        </List>
      </Paper>

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={closeMenu}>
        <MenuItem onClick={onOpenDocs}>Dokumentacja pakietu</MenuItem>
        <MenuItem onClick={onInquiry}>Zapytanie</MenuItem>
      </Menu>

      <MobileInquiryForm
        open={inquiryOpen}
        onClose={()=> setInquiryOpen(false)}
        projectId={projectId}
        projectName={projectName}
        initialPackage={selected || undefined}
        packages={packages}
      />
    </Box>
  );
};

export default MobilePackagesList;


